import { useCallback, useEffect, useMemo, useState } from 'react';
import { parkingQueueApi } from '../../api/parkingQueueApi';
import { parkingApi } from '../../api/parkingApi';
import { unitsApi } from '../../api/unitsApi';
import { leasesApi } from '../../api/leasesApi';
import { getParkingQueueConfig } from '../../api/parkingQueueConfigApi';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { orderRows, rowFacts, QueueRowFacts } from '../../Helpers/parkingQueueUtils';
import { isLevelBlueprintSource, parkingAreaDisplayName } from '../../Helpers/parkingUtils';
import { ParkingQueueEntry } from '../../types/parkingQueue';
import type { BuildingStructure } from '../../types/protocols';
import { DEFAULT_PARKING_QUEUE_CONFIG, ParkingQueueConfig } from '../../types/parkingQueueConfig';
import { ParkingArea } from '../../types/parking';
import { Unit } from '../../types/units';
import { Lease } from '../../types/leases';

export interface UseParkingQueueReturn {
    entries: ParkingQueueEntry[];
    /** Every entry's row facts, in the configured standard order. */
    rows: QueueRowFacts[];
    leases: Lease[];
    config: ParkingQueueConfig;
    areasByBuilding: { [buildingKey: string]: { [areaKey: string]: ParkingArea } };
    /** For LeaseModal's unit picker in the queue lease flow. */
    unitsByBuilding: { [buildingKey: string]: { [unitKey: string]: Unit } };
    /** Rooms and levels, loaded only for buildings with an area in level-drawing mode (for the drawing thumbnails). */
    structureByBuilding: { [buildingKey: string]: Pick<BuildingStructure, 'rooms' | 'levels'> };
    buildingName: (buildingKey: string) => string;
    areaLabel: (buildingKey: string, areaKey: string) => string;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * Everything the Parkeringskö pages show: all entries, all leases (for spots
 * held and unit suggestions), the order config and every building's parking
 * areas and units for labels. Loaded once; `refresh` reloads all of it.
 */
export const useParkingQueue = (): UseParkingQueueReturn => {
    const { buildings } = useBuildingsData();
    const [entries, setEntries] = useState<ParkingQueueEntry[]>([]);
    const [leases, setLeases] = useState<Lease[]>([]);
    const [config, setConfig] = useState<ParkingQueueConfig>(DEFAULT_PARKING_QUEUE_CONFIG);
    const [areasByBuilding, setAreasByBuilding] = useState<UseParkingQueueReturn['areasByBuilding']>({});
    const [structureByBuilding, setStructureByBuilding] = useState<UseParkingQueueReturn['structureByBuilding']>({});
    const [unitsByBuilding, setUnitsByBuilding] = useState<UseParkingQueueReturn['unitsByBuilding']>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const buildingKeys = useMemo(() => (buildings ?? []).map(b => b.key), [buildings]);
    const buildingKeysSignature = buildingKeys.join(',');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [queueEntries, allLeases, queueConfig, perBuilding] = await Promise.all([
                parkingQueueApi.getAll(),
                leasesApi.getAll(),
                getParkingQueueConfig(),
                Promise.all(buildingKeys.map(async key => {
                    const [areas, units] = await Promise.all([parkingApi.getAreas(key), unitsApi.getUnits(key)]);
                    const structure = Object.values(areas).some(isLevelBlueprintSource) ? await parkingApi.getBlueprintStructure(key) : undefined;
                    return { key, areas, units, structure };
                })),
            ]);
            setEntries(queueEntries);
            setLeases(allLeases);
            setConfig(queueConfig);
            const areas: UseParkingQueueReturn['areasByBuilding'] = {};
            const units: UseParkingQueueReturn['unitsByBuilding'] = {};
            const structures: UseParkingQueueReturn['structureByBuilding'] = {};
            perBuilding.forEach(b => {
                areas[b.key] = b.areas;
                units[b.key] = b.units;
                if (b.structure) structures[b.key] = b.structure;
            });
            setAreasByBuilding(areas);
            setUnitsByBuilding(units);
            setStructureByBuilding(structures);
        } catch (e) {
            console.error('Failed to load parking queue', e);
            setError('Kunde inte läsa parkeringskön');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingKeysSignature]);

    const summariesLoaded = buildings !== undefined;
    useEffect(() => {
        if (!summariesLoaded) return;
        void refresh();
    }, [summariesLoaded, refresh]);

    const rows = useMemo(
        () => orderRows(entries.map(entry => rowFacts(entry, leases)), config),
        [entries, leases, config]
    );

    const buildingName = useCallback(
        (buildingKey: string) => (buildings ?? []).find(b => b.key === buildingKey)?.name ?? 'Okänd byggnad',
        [buildings]
    );
    const areaLabel = useCallback((buildingKey: string, areaKey: string) => {
        const area = areasByBuilding[buildingKey]?.[areaKey];
        return area ? parkingAreaDisplayName(area) : 'Okänt område';
    }, [areasByBuilding]);

    return { entries, rows, leases, config, areasByBuilding, unitsByBuilding, structureByBuilding, buildingName, areaLabel, loading, error, refresh };
};
